import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, CheckCheck } from "lucide-react";
import { formatDistanceToNow, parseISO } from "date-fns";
import { frCA } from "date-fns/locale";
import { toast } from "sonner";
import api, { API } from "@/lib/api";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

export default function NotificationBell() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);

  const loadCount = useCallback(() => {
    api.get("/notifications/unread-count").then(({ data }) => setUnread(data.count || 0)).catch(() => {});
  }, []);

  const loadList = useCallback(() => {
    api.get("/notifications/history", { params: { limit: 8 } })
      .then(({ data }) => { setItems(data.items || []); setUnread(data.unread || 0); })
      .catch(() => {});
  }, []);

  useEffect(() => {
    loadCount();
    const t = setInterval(loadCount, 60000);
    return () => clearInterval(t);
  }, [loadCount]);

  // Flux temps réel (SSE) — met à jour la pastille instantanément à la réception
  useEffect(() => {
    if (typeof EventSource === "undefined") return undefined;
    const es = new EventSource(`${API}/notifications/stream`, { withCredentials: true });
    es.onmessage = (e) => {
      let ev;
      try { ev = JSON.parse(e.data); } catch { return; }
      if (typeof ev.unread === "number") setUnread(ev.unread);
      if (ev.kind === "notification") {
        if (open) loadList();
        window.dispatchEvent(new CustomEvent("pi-notify", { detail: ev }));
        if (ev.urgent) toast.warning(ev.title, { description: ev.body });
      }
    };
    es.onerror = () => { /* EventSource se reconnecte automatiquement */ };
    return () => es.close();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => { if (open) loadList(); }, [open, loadList]);

  const openItem = async (n) => {
    if (!n.read) {
      try { await api.post(`/notifications/${n.id}/read`); } catch (e) { /* ignore */ }
    }
    setOpen(false);
    navigate(n.url || "/notifications");
  };

  const markAll = async () => {
    try { await api.post("/notifications/read-all"); } catch (e) { /* ignore */ }
    loadList();
    loadCount();
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button data-testid="notification-bell"
                className="relative p-2 rounded-md text-slate-500 hover:bg-slate-100 hover:text-sky-700">
          <Bell className="h-4 w-4" />
          {unread > 0 && (
            <span data-testid="notification-unread-badge"
                  className="absolute -top-0.5 -right-0.5 text-[9px] font-bold bg-red-600 text-white
                             rounded-full min-w-[16px] h-4 px-1 flex items-center justify-center">
              {unread > 9 ? "9+" : unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0" data-testid="notification-popover">
        <div className="flex items-center justify-between px-4 py-2.5 border-b border-slate-100">
          <span className="text-sm font-semibold text-slate-800">Notifications</span>
          {unread > 0 && (
            <button onClick={markAll} data-testid="notif-mark-all"
                    className="flex items-center gap-1 text-[11px] text-sky-700 hover:underline">
              <CheckCheck className="h-3.5 w-3.5" /> Tout marquer lu
            </button>
          )}
        </div>
        <div className="max-h-80 overflow-y-auto">
          {items.length === 0 ? (
            <div className="px-4 py-8 text-center text-sm text-slate-400">Aucune notification</div>
          ) : items.map((n) => (
            <button key={n.id} onClick={() => openItem(n)} data-testid={`notif-item-${n.id}`}
                    className={`w-full text-left px-4 py-2.5 border-b border-slate-50 hover:bg-slate-50 transition-colors
                                ${n.read ? "" : "bg-sky-50/60"}`}>
              <div className="flex items-start gap-2">
                {!n.read && <span className="mt-1.5 h-2 w-2 rounded-full bg-sky-500 flex-shrink-0" />}
                <div className={`min-w-0 flex-1 ${n.read ? "pl-4" : ""}`}>
                  <div className="text-[13px] font-medium text-slate-800 truncate">{n.title}</div>
                  <div className="text-[11px] text-slate-500 line-clamp-2">{n.body}</div>
                  <div className="text-[10px] text-slate-400 mt-0.5">
                    {n.sent_at ? formatDistanceToNow(parseISO(n.sent_at), { addSuffix: true, locale: frCA }) : ""}
                  </div>
                </div>
              </div>
            </button>
          ))}
        </div>
        <button onClick={() => { setOpen(false); navigate("/notifications"); }}
                data-testid="notif-see-all"
                className="w-full text-center py-2.5 text-xs font-semibold text-sky-700 hover:bg-slate-50 border-t border-slate-100">
          Voir toutes les notifications
        </button>
      </PopoverContent>
    </Popover>
  );
}
